// ============================================================
// HABIT OS — Bibliothek: Schlaf-Optimierung
// Zirkadiane Rhythmen · Schlafarchitektur · Umgebung
// Ernährung & Supplemente · Morgen-Protokoll
// ============================================================

export const LIBRARY_SLEEP_OPTIMIZATION = {
  id: "sleep_optimization",
  label: "Schlaf-Optimierung",
  icon: "😴",
  subcategories: [

    // ── ZIRKADIANER RHYTHMUS ─────────────────
    {
      id: "sleep_circadian",
      label: "Zirkadianer Rhythmus",
      icon: "🌅",
      habits: [
        {
          id: "hab_sleep_morning_light",
          label: "Morgensonnenlicht in den ersten 30–60 Min.",
          time: "morning",
          icon: "☀️",
          why: "Morgenlicht (idealerweise direkte Sonneneinstrahlung) triggert den Cortisol Awakening Response (CAR) — einen natürlichen, gesunden Cortisol-Peak der den gesamten Tagesrhythmus setzt. Licht aktiviert ipRGC-Zellen in der Netzhaut die direkt den Suprachiasmatischen Nucleus (SCN) — die Master-Uhr des Körpers — synchronisieren. 10.000 Lux Sonnenlicht vs. 500 Lux Innenbeleuchtung: Die Differenz ist enorm. Frühes Sonnenlicht setzt außerdem den Melatonin-Timer: Melatonin wird exakt 12–14 Stunden später ausgeschüttet.",
          source: "Huberman A., Huberman Lab Podcast, 2021 · Czeisler et al., Science, 1989",
          dose: "10–30 Min. Tageslicht im Freien in der ersten Stunde nach dem Aufwachen. Nie direkt in die Sonne schauen.",
        },
        {
          id: "hab_sleep_consistent_wake",
          label: "Feste Aufwachzeit 7 Tage pro Woche",
          time: "morning",
          icon: "⏰",
          why: "Die Aufwachzeit — nicht die Schlafenszeit — ist der stärkste Anker des zirkadianen Rhythmus. Eine variable Aufwachzeit (auch nur 1–2 Stunden Unterschied am Wochenende) erzeugt 'Social Jetlag' — ein chronischer Zustand der mit erhöhtem Diabetes-Risiko, Übergewicht und Depression korreliert. Konsistente Aufwachzeit stabilisiert Cortisol, Melatonin, Körpertemperatur und alle weiteren zirkadianen Rhythmen.",
          source: "Roenneberg et al., Current Biology, 2012 · Depner et al., Current Biology, 2019",
          dose: "Jeden Tag zur selben Zeit aufstehen — auch am Wochenende, auch nach schlechtem Schlaf. Die Schlafenszeit kann leicht variieren, die Aufwachzeit nicht.",
        },
        {
          id: "hab_sleep_temperature_drop",
          label: "Körpertemperatur 1–3°C senken für Einschlafen",
          time: "night",
          icon: "🌡️",
          why: "Einschlafen ist physiologisch an einen Abfall der Körperkerntemperatur gekoppelt. Der Körper muss 1–3°C abkühlen um in den Schlaf zu kommen. Paradoxerweise hilft ein warmes Bad (37–39°C) 1–2 Stunden vor dem Schlafen: Wärme zieht Blut an die Körperoberfläche (Vasodilatation), was die Wärme schnell abführt und die Kerntemperatur sinkt. Zimmertemperatur 16–19°C ist optimal. Socken im Bett beschleunigen Einschlafen — fördern periphere Vasodilatation.",
          source: "Raymann et al., Brain, 2008 · Kräuchi et al., Nature, 1999",
          dose: "Schlafzimmer auf 16–19 °C kühlen. Optional ein warmes Bad 1–2 Std. vor dem Schlafen.",
        },
        {
          id: "hab_sleep_light_sunset",
          label: "Abendlicht dimmen ab Sonnenuntergang",
          time: "night",
          icon: "🌆",
          why: "Künstliches Licht nach Sonnenuntergang — besonders kurzwelliges blaues Licht (450–480nm) von Bildschirmen und LED-Lampen — supprimiert Melatonin-Ausschüttung um bis zu 50% und verzögert sie um 2–3 Stunden. Das Gehirn interpretiert blaues Licht als 'Mittag' und verschiebt die innere Uhr. Lösung: Alle Lichter nach Sonnenuntergang auf warm/orange dimmen. Bildschirm-Nachtmodus oder blaue Lichtfilter-Brillen. Kerzen und Salzlampen haben keinen signifikanten Melatonin-Effekt.",
          source: "Gooley et al., Journal of Clinical Endocrinology & Metabolism, 2011 · Chang et al., PNAS, 2015",
          dose: "Ab 1–2 Std. vor dem Schlafen: Deckenlichter aus, nur warme Lampen (2700K oder wärmer). Bildschirme auf Nachtmodus. Oder Blaulicht-Filtergläser. Ideal: ab 21 Uhr nur noch Kerzen/warmes Licht.",
        },
      ],
    },

    // ── SCHLAFARCHITEKTUR ────────────────────
    {
      id: "sleep_architecture",
      label: "Schlafarchitektur verstehen",
      icon: "🧠",
      habits: [
        {
          id: "hab_sleep_90min_cycles",
          label: "90-Minuten-Zyklen: Weckzeit optimieren",
          time: "night",
          icon: "🔄",
          why: "Schlaf besteht aus 90-Minuten-Zyklen (NREM1 → NREM2 → NREM3 → REM). Aufwachen zwischen Zyklen ist erholsamer als Aufwachen mitten in einem Tiefschlaf-Zyklus. Frühe Zyklen (22–2 Uhr) sind Tiefschlaf-dominiert (Körperreparatur, Wachstumshormon). Späte Zyklen (2–6 Uhr) sind REM-dominiert (Gedächtnis, Emotionsverarbeitung, Kreativität). 7,5 Stunden (5 Zyklen) oder 9 Stunden (6 Zyklen) sind optimaler als 8 Stunden die mitten in einem Zyklus enden.",
          source: "Walker M., Why We Sleep, 2017 · Dijk & Czeisler, Journal of Neuroscience, 1995",
          dose: "Weckzeit berechnen: Einschlafzeit + 7,5h oder + 9h. App 'Sleep Cycle' oder 'Oura Ring' erkennt Schlafphasen automatisch. Wecker auf leichten Schlaf setzen wenn möglich.",
        },
        {
          id: "hab_sleep_rem_protection",
          label: "REM-Schlaf schützen: Alkohol & Cannabis vermeiden",
          time: "night",
          icon: "🧬",
          why: "Alkohol ist der stärkste REM-Schlaf-Unterdrücker bekannt. Selbst 1–2 Drinks reduzieren REM-Schlaf um 20–25%. REM ist essentiell für: emotionale Verarbeitung, Gedächtniskonsolidierung, Kreativität und psychische Gesundheit. Menschen die regelmäßig Alkohol trinken haben chronisch reduzierte REM-Phasen — mit messbaren kognitiven und emotionalen Konsequenzen. Cannabis (THC) unterdrückt REM ebenfalls stark. CBD allein hat diesen Effekt nicht.",
          source: "Ebrahim et al., Alcoholism: Clinical and Experimental Research, 2013 · Babson et al., Current Psychiatry Reports, 2017",
          dose: "Kein Alkohol innerhalb von 4–6 Stunden vor dem Schlafen.",
        },
        {
          id: "hab_sleep_nap_protocol",
          label: "Power Nap: 20 Min. oder 90 Min. — nie dazwischen",
          time: "flexible",
          icon: "😪",
          why: "Der 'Nap Sweet Spot': 10–20 Minuten erreicht NREM2 (erholsam, kein Schlafträgheit/Grogginess danach). 90 Minuten durchläuft einen vollständigen Zyklus. 30–60 Minuten ist das Worst-Case-Szenario: weckt aus Tiefschlaf (NREM3) → Schlafträgheit, schlechte Laune, Beeinträchtigung der Nachtschlaf-Qualität. Optimale Nap-Zeit: 13–15 Uhr (natürlicher zirkadianer Tief). Koffein-Nap: Espresso trinken, sofort 20 Min. schlafen — Koffein wirkt genau wenn du aufwachst.",
          source: "Mednick et al., Nature Neuroscience, 2003 · Lovato & Lack, Progress in Brain Research, 2010",
          dose: "Entweder 20 Min. (Wecker stellen!) oder 90 Min. Nie ohne Wecker. Koffein-Nap: 1 Espresso direkt vor dem 20-Min-Nap. Zwischen 13–15 Uhr.",
        },
      ],
    },

    // ── SCHLAFUMGEBUNG ───────────────────────
    {
      id: "sleep_environment",
      label: "Schlafumgebung optimieren",
      icon: "🛏️",
      habits: [
        {
          id: "hab_sleep_dark_room",
          label: "Totale Dunkelheit im Schlafzimmer",
          time: "night",
          icon: "🌑",
          why: "Selbst kleine Lichtmengen (Standby-LEDs, Straßenlaternen durch Vorhänge) erreichen die Netzhaut durch geschlossene Lider und unterdrücken Melatonin. Studie: Schlafen mit moderatem Licht erhöht Herzfrequenz, Insulinresistenz und reduziert Tiefschlaf-Anteile. Das Schlafzimmer sollte so dunkel sein dass du die eigene Hand nicht siehst. Schlafmasken sind eine einfache Alternative.",
          source: "Mason et al., PNAS, 2022",
          dose: "Blackout-Vorhänge oder Schlafmaske. Alle LEDs abkleben (Ladekabel, Router, TV-Standby). Handykein Display beim Laden abdecken.",
        },
        {
          id: "hab_sleep_no_phone_bedroom",
          label: "Handy aus dem Schlafzimmer verbannen",
          time: "night",
          icon: "📵",
          why: "Das Handy im Schlafzimmer kann durch Licht, Benachrichtigungen und spätes Scrollen den Schlaf stören.",
          source: "Hysing et al., BMJ Open, 2015 · Exelmans & Van den Bulck, Journal of Sleep Research, 2015",
          dose: "Handy laden außerhalb des Schlafzimmers. Wecker: eigener Wecker oder Smartwatch. Wenn Handy im Zimmer nötig: in Flugzeugmodus, Display abgedeckt, in einer Schublade.",
        },
        {
          id: "hab_sleep_noise",
          label: "Weißes Rauschen oder Stille für tiefen Schlaf",
          time: "night",
          icon: "🔊",
          why: "Das Gehirn hört auch im Schlaf weiter und reagiert auf plötzliche Geräusche mit Micro-Arousals die die Schlafarchitektur fragmentieren — auch wenn man sich nicht daran erinnert. Weißes Rauschen (oder Pink/Brown Noise) maskiert variable Umgebungsgeräusche durch konstantes Hintergrundrauschen. Studien zeigen Reduktion von Einschlafzeit um 38% und weniger nächtliche Aufwachreaktionen. WICHTIG: Lautstärke unter 65 dB halten — zu lautes Weißes Rauschen kann Hörschäden verursachen.",
          source: "Messineo et al., Frontiers in Neurology, 2017",
          dose: "App: 'Calm', 'White Noise+', oder dediziertes Gerät. Lautstärke: 50–60 dB (Gesprächslautstärke). Pink Noise oder Brown Noise sind angenehmer als reines weißes Rauschen.",
        },
      ],
    },

    // ── SCHLAF-ERNÄHRUNG ─────────────────────
    {
      id: "sleep_nutrition",
      label: "Ernährung & Schlaf",
      icon: "🍽️",
      habits: [
        {
          id: "hab_sleep_no_late_eating",
          label: "Letzte Mahlzeit 3 Std. vor dem Schlafen",
          time: "night",
          icon: "🍽️",
          why: "Eine große Mahlzeit kurz vor dem Schlafen kann den Schlaf stören. Viele schlafen besser, wenn die letzte größere Mahlzeit einige Stunden zurückliegt.",
          source: "St-Onge et al., Obesity, 2016 · Kinsey & Ormsbee, Nutrients, 2015",
          dose: "Letzte vollständige Mahlzeit mindestens 3 Stunden vor dem Schlafen. Ausnahme: kleines protein-reiches Bedtime-Snack (Hüttenkäse) ist okay — Casein erhöht sogar Schlafqualität.",
        },
        {
          id: "hab_sleep_tryptophan_foods",
          label: "Tryptophan-reiche Lebensmittel am Abend",
          time: "night",
          icon: "🥛",
          why: "Tryptophan ist die Aminosäure-Vorstufe von Serotonin und Melatonin. Der Weg: Tryptophan → 5-HTP → Serotonin → Melatonin. Tryptophan-reiche Lebensmittel am Abend erhöhen die Melatonin-Vorstufen-Verfügbarkeit. Trick: Tryptophan braucht Insulin um die Blut-Hirn-Schranke zu passieren (verdrängt konkurrierende Aminosäuren). Kleine Menge Kohlenhydrate am Abend mit Tryptophan-Quellen = bessere Aufnahme ins Gehirn.",
          source: "Hartmann, Journal of Nervous and Mental Disease, 1982 · Peuhkuri et al., Nutrition Research, 2012",
          dose: "Abendmahlzeit oder Snack: Hüttenkäse, Kürbiskerne, Banane, Kirschen (natürliches Melatonin), Walnüsse, Haferflocken. Kleine Menge Kohlenhydrate dazu für Transport ins Gehirn.",
        },
        {
          id: "hab_sleep_caffeine_cutoff",
          label: "Koffein-Cutoff: 8–10 Stunden vor dem Schlafen",
          time: "flexible",
          icon: "☕",
          why: "Koffein blockiert Adenosin-Rezeptoren — Adenosin ist der Schlaf-Druck-Botenstoff der sich über den Tag akkumuliert. Koffein verhindert dieses Signal aber beseitigt den akkumulierten Adenosin nicht. Die Halbwertszeit von Koffein beträgt 5–7 Stunden — nach 10 Stunden sind noch 25% im Blut. Ein 15-Uhr-Kaffee bedeutet um Mitternacht noch signifikanten Koffein-Spiegel. Auch wenn man 'trotzdem schläft': REM-Schlaf und Tiefschlaf sind messbar reduziert.",
          source: "Drake et al., Journal of Clinical Sleep Medicine, 2013 · Landolt et al., Neuropsychopharmacology, 1995",
          dose: "Letzter Kaffee/Tee spätestens 13–14 Uhr (bei 23 Uhr Schlafenszeit). Bei Schlafproblemen: 12 Uhr als Cutoff testen. Entkoffeinierter Kaffee enthält noch 2–15mg Koffein.",
        },
      ],
    },

    // ── SCHLAF-SUPPLEMENTE ───────────────────
    {
      id: "sleep_supplements",
      label: "Schlaf-Supplemente",
      icon: "💊",
      habits: [
        {
          id: "hab_sleep_melatonin_micro",
          label: "Melatonin: Weniger ist mehr",
          time: "night",
          icon: "🌙",
          why: "Melatonin ist ein körpereigenes Hormon, das dem Körper den Abend signalisiert. Melatonin trägt dazu bei, die Einschlafzeit zu verkürzen (bei 1 mg kurz vor dem Schlafengehen).",
          source: "Zhdanova et al., Sleep, 1998 · Brzezinski et al., Sleep Medicine Reviews, 2005",
          dose: "Höchstens 1 mg laut Herstellerangabe und vorher ärztlich abklären. Anhaltende Schlafstörungen ärztlich untersuchen lassen.",
        },
        {
          id: "hab_sleep_ashwagandha",
          label: "Ashwagandha am Abend",
          time: "night",
          icon: "🌿",
          why: "Ashwagandha ist eine traditionelle ayurvedische Pflanze. Wegen möglicher Leber- und Schilddrüseneffekte vorher ärztlich abklären.",
          source: "Chandrasekhar et al., Indian Journal of Psychological Medicine, 2012 · Langade et al., Medicine, 2019",
          dose: "Nur laut Herstellerangabe und nach ärztlicher Rücksprache.",
        },
        {
          id: "hab_sleep_glycine",
          label: "Glycin am Abend",
          time: "night",
          icon: "🧬",
          why: "Glycin ist eine Aminosäure, die u. a. in Kollagen vorkommt. Kleine Studien deuten auf eine bessere subjektive Schlafqualität hin; die Datenlage ist begrenzt.",
          source: "Yamadera et al., Sleep and Biological Rhythms, 2007 · Bannai et al., Frontiers in Neurology, 2012",
          dose: "Glycin laut Herstellerangabe in warmem Wasser oder Kräutertee, 30–60 Min. vor dem Schlafen.",
        },
      ],
    },
  ],
};